import { Fragment, useContext } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PersonIcon from '@mui/icons-material/Person';
import CheckIcon from '@mui/icons-material/Check';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { PredictContext, DEMO_USERS } from '~/providers/PredictProvider';

interface Scenario {
  id: string;
  title: string;
  description: string;
  patientName: string;
  changes: string[];
}

const BASE_SCENARIOS: Scenario[] = [
  {
    id: 'linda-martinez-upgrade',
    title: 'Clinical Update: Low to High Confidence',
    description: 'Simulates Linda Martinez receiving clinical updates that upgrade her from low confidence (45) to high confidence (82) for SNF at Home discharge.',
    patientName: 'Linda Martinez',
    changes: [
      'Score changes from 45 to 82',
      'Program changes to SNF at Home',
      'Clinical updates: PT/OT evaluation, O2 weaned, caregiver documented',
      'Stage transitions to Triage',
      'Change indicator: Clinical Update',
    ],
  },
  {
    id: 'william-johnson-disqualification',
    title: 'Disqualification & Re-evaluation',
    description: 'Simulates William Johnson being re-evaluated after daughter arranges FMLA leave. Patient moves from Disqualified back to Triage when caregiver becomes available.',
    patientName: 'William Johnson',
    changes: [
      'Caregiver situation changes: Daughter arranges FMLA leave',
      'Score improves from 42 to 75',
      'Stage changes from Disqualified to Triage',
      'Program recommendation: Home Health (moderate confidence)',
      'Updated decision factors reflect caregiver availability',
      'Change indicator: Re-evaluation',
    ],
  },
  {
    id: 'maria-garcia-respiratory-distress',
    title: 'Acute Deterioration: Disqualification',
    description: 'Simulates Maria Garcia developing acute respiratory failure requiring BiPAP and ICU-level care. Patient becomes disqualified from home programs due to high acuity.',
    patientName: 'Maria Garcia',
    changes: [
      'Acute respiratory distress requiring BiPAP support',
      'Transfer to ICU from Oncology floor',
      'FiO2 60% with oxygen saturations 88-90%',
      'Altered mental status, unable to protect airway',
      'Stage changes from Acceptance to Disqualified',
      'Disqualification reason: Acuity too high',
      'Change indicator: Clinical Deterioration',
    ],
  },
  {
    id: 'angela-martinez-complication',
    title: 'Clinical Complication: PIV Line Issue',
    description: 'Simulates Angela Martinez developing PIV line complications requiring line removal, changing from Home Health candidate (85) to needing SNF at Home placement (78).',
    patientName: 'Angela Martinez',
    changes: [
      'PIV line removed due to phlebitis',
      'Unable to place new peripheral access',
      'Score drops from 85 to 78',
      'Program changes from Home Health to SNF at Home',
      'Clinical update: needs continued IV therapy with PICC vascular access; no caregiver available',
      'Workflow resets to Triage',
      'Change indicator: Clinical Deterioration',
    ],
  },
  {
    id: 'thomas-anderson-icu-recovery',
    title: 'ICU Recovery: Floor Transfer',
    description: 'Simulates Thomas Anderson stabilizing and transferring from ICU to Medical Floor. Patient becomes eligible after respiratory improvement and extubation.',
    patientName: 'Thomas Anderson',
    changes: [
      'Department changes from ICU to Pulmonology',
      'Respiratory status: Extubated, stable on 2L O2',
      'Score improves from 35 to 72',
      'Program changes to SNF at Home',
      'Stage changes from Disqualified to Triage',
      'Change indicator: Clinical Improvement',
    ],
  },
];

const HAH_SCENARIOS: Scenario[] = [
  {
    id: 'david-park-hah-upgrade',
    title: 'Hospital at Home ED Diversion: Clinical Candidacy Confirmed',
    description: 'David Park arrives to the ED with a moderate COPD exacerbation. After 90 minutes of treatment, Hospital at Home candidacy score upgrades from 48 to 81.',
    patientName: 'David Park',
    changes: [
      'Hospital at Home clinical candidacy score improves from 48 to 81',
      'Program recommendation changes to Hospital at Home',
      'O2 weaned from 3L to 1.5L after 90 min of ED nebulizer + steroid treatment',
      'Vitals normalized: HR 82, RR 18 (from 96/22 on arrival)',
      'No escalation to BiPAP or ICU — moderate exacerbation confirmed',
      'Hospital at Home eligibility and social screening workflow triggered',
      'Without Hospital at Home: patient would be admitted to floor for 4–5 days',
      'Change indicator: Upgraded',
    ],
  },
];

const getRoleLabel = (role: string) => {
  const formatted = role.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (l: string) => l.toUpperCase());
  return formatted.replace(/\bRn\b/g, 'RN');
};

export const PredictDemoContent: React.FC = () => {
  const navigate = useNavigate();
  const context = useContext(PredictContext);

  if (!context) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Navigate to the Predict section to use these controls.
        </Typography>
        <Button variant="contained" onClick={() => navigate('/predict/all-patients')}>
          Go to Predict
        </Button>
      </Box>
    );
  }

  const { triggerDemoScenario, resetDemoScenario, currentUser, switchDemoUser, demoMode, setDemoMode } = context;
  const activeScenarios = demoMode === 'hah-plus' ? [...BASE_SCENARIOS, ...HAH_SCENARIOS] : BASE_SCENARIOS;

  return (
    <Box>
      <Tabs
        value={demoMode}
        onChange={(_, v) => setDemoMode(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab label="No Hospital at Home" value="no-hah" sx={{ textTransform: 'none' }} />
        <Tab label="Hospital at Home+" value="hah-plus" sx={{ textTransform: 'none' }} />
      </Tabs>

      {currentUser && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            bgcolor: 'primary.main',
            color: 'white',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <PersonIcon sx={{ fontSize: 40 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body1" fontWeight={600} noWrap>{currentUser.name}</Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }} noWrap>{getRoleLabel(currentUser.role)}</Typography>
          </Box>
        </Box>
      )}

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: 'text.secondary' }}>
          SWITCH PERSONA
        </Typography>
        <List dense sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1, py: 0 }}>
          {DEMO_USERS.map((user, index) => (
            <Fragment key={user.id}>
              {index > 0 && <Divider />}
              <ListItem disablePadding>
                <ListItemButton
                  selected={user.id === currentUser?.id}
                  onClick={() => switchDemoUser(user)}
                  sx={{ py: 1, px: 1.5 }}
                >
                  {user.id === currentUser?.id && (
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                  )}
                  <ListItemText
                    inset={user.id !== currentUser?.id}
                    primary={<Typography variant="body2" fontWeight={500}>{user.name}</Typography>}
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {getRoleLabel(user.role)} · {user.clientOrganizations.length}{' '}
                        {user.clientOrganizations.length === 1 ? 'hospital' : 'hospitals'}
                      </Typography>
                    }
                    sx={{ my: 0 }}
                  />
                </ListItemButton>
              </ListItem>
            </Fragment>
          ))}
        </List>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: 'text.secondary' }}>
        CLINICAL SCENARIOS
      </Typography>
      <List sx={{ py: 0 }}>
        {activeScenarios.map((scenario, index) => (
          <ListItem
            key={scenario.id}
            disablePadding
            sx={{ mb: index < activeScenarios.length - 1 ? 1 : 0 }}
          >
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1.5,
                bgcolor: 'background.paper',
                width: '100%',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="body2" fontWeight={600}>{scenario.title}</Typography>
                <Chip label={scenario.patientName} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {scenario.description}
              </Typography>
              <Box component="ul" sx={{ pl: 2, mt: 0.5, mb: 1 }}>
                {scenario.changes.map((change, idx) => (
                  <Typography
                    key={idx}
                    component="li"
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.7rem', lineHeight: 1.4 }}
                  >
                    {change}
                  </Typography>
                ))}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  onClick={() => resetDemoScenario(scenario.id)}
                  startIcon={<RestartAltIcon fontSize="small" />}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Reset
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => triggerDemoScenario(scenario.id)}
                  startIcon={<PlayArrowIcon fontSize="small" />}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Trigger
                </Button>
              </Box>
            </Box>
          </ListItem>
        ))}
      </List>

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<RestartAltIcon />}
          onClick={() => activeScenarios.forEach(s => resetDemoScenario(s.id))}
        >
          Reset All
        </Button>
        <Button
          variant="contained"
          startIcon={<PlayArrowIcon />}
          onClick={() => activeScenarios.forEach(s => triggerDemoScenario(s.id))}
        >
          Trigger All
        </Button>
      </Box>
    </Box>
  );
};
